import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { authService } from '@/services/api'
import { registerFormSchema, defaultRegisterValues, REGISTER_STEPS } from './RegisterForm.static'
import type { RegisterFormValues, RegisterStep } from './RegisterForm.static'
import type { ApiError } from '@/types'

export function useRegisterForm() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [currentStep, setCurrentStep] = useState<RegisterStep>(REGISTER_STEPS.PERSONAL)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: defaultRegisterValues,
    mode: 'onBlur',
  })

  const registerMutation = useMutation({
    mutationFn: authService.register,
    onSuccess: (data) => {
      setAuth(data.user, data.facility)
      navigate('/verify-email-pending')
    },
    onError: (error: AxiosError<ApiError>) => {
      const status = error.response?.status
      const message =
        status === 409
          ? t('errors.emailExists')
          : status === 429
            ? t('errors.rateLimitExceeded')
            : t('errors.registrationFailed')
      form.setError('root', {
        type: 'manual',
        message,
      })
    },
  })

  const validatePersonalStep = async () => {
    const result = await form.trigger([
      'email',
      'password',
      'confirmPassword',
      'firstName',
      'lastName',
      'privacyAccepted',
    ])
    return result
  }

  const handleNext = async () => {
    if (currentStep === REGISTER_STEPS.PERSONAL) {
      const isValid = await validatePersonalStep()
      if (isValid) {
        setCurrentStep(REGISTER_STEPS.FACILITY)
      }
    }
  }

  const handleBack = () => {
    if (currentStep === REGISTER_STEPS.FACILITY) {
      setCurrentStep(REGISTER_STEPS.PERSONAL)
    }
  }

  const onSubmit = (data: RegisterFormValues) => {
    const facilityEmail = data.facility.email === '' ? undefined : data.facility.email

    registerMutation.mutate({
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone || undefined,
      facility: {
        name: data.facility.name,
        address: data.facility.address,
        city: data.facility.city,
        postalCode: data.facility.postalCode || undefined,
        phone: data.facility.phone,
        email: facilityEmail,
        bedCount: data.facility.bedCount,
        bulstat: data.facility.bulstat || undefined,
        registrationNumber: data.facility.registrationNumber || undefined,
      },
    })
  }

  const toggleShowPassword = () => setShowPassword((prev) => !prev)
  const toggleShowConfirmPassword = () => setShowConfirmPassword((prev) => !prev)

  return {
    t,
    form,
    currentStep,
    isLoading: registerMutation.isPending,
    showPassword,
    showConfirmPassword,
    onSubmit,
    handleNext,
    handleBack,
    toggleShowPassword,
    toggleShowConfirmPassword,
    REGISTER_STEPS,
  }
}
